import qs from "query-string";
import { Native } from "@hb/mbridge";
import { timeFormat } from "../../common/utils/time";
import env, {
  getAppEnv,
  isWeixin,
  isHelloFamilyApp,
  isEasyBikeApp,
  isIOS,
} from "../../common/env/env";
import { goCouponPage, showThirdPage } from "../../common/utils/jumpUrl";
import { uiMgr } from "../../common/uiMgr";
import { appQueryConfig, getUrlParams } from "../../common/utils/url";
import {
  Equity,
  EquityType,
  EquityCouponType,
  OrderEquity,
  PromoIntervalItemType,
} from "../store/modules/ticket";
import { fetchCouponGouseConfig } from "../apis/ticketApis";

/** 权益描述类型 */
type EquityDescType =
  | "discountAmount"
  | "threshold"
  | "validDate"
  | "validDays";
type EquityDescMap = Partial<Record<EquityDescType, string>>;

export type CouponLeft = {
  /** 数字部分 */
  num: string;
  /** 文字部分 */
  word: string;
};

// 提取阶梯价格计算逻辑
const getLadderPriceRange = (promoInterval?: PromoIntervalItemType)
: { min: number; max: number } | null => {
  if (!promoInterval?.promoInfoList?.length) {
    return null;
  }
  const values = promoInterval.promoInfoList.map((item) => item.value);
  return {
    min: Math.min(...values),
    max: Math.max(...values),
  };
};

// 处理券类型优惠金额以及优惠文案
export const getCouponBaseInfo = (equity: Equity) => {
  // 类型守卫：只有virtualType为优惠券类型才有 couponType
  if (equity.virtualType !== EquityType.COUPON) {
    return { num: '', word: '' };
  }

  const {
    couponType, promoInterval, discount, worth,
  } = equity;

  switch (couponType) {
    // 阶梯减
    case EquityCouponType.LADDER_SUB: {
      const range = getLadderPriceRange(promoInterval);
      if (!range) {
        return { num: '', word: '元' };
      }
      return {
        num: `${range.min}-${range.max}`,
        word: '元',
      };
    }

    // 阶梯折
    case EquityCouponType.LADDER_DISCOUNT: {
      const range = getLadderPriceRange(promoInterval);
      if (!range) {
        return { num: '', word: '折' };
      }
      return {
        num: `${range.max}-${range.min}`,
        word: '折',
      };
    }

    // 折扣类型（折扣、免单）
    case EquityCouponType.DISCOUNT:
    case EquityCouponType.FREE: {
      return {
        num: `${discount || ''}`,
        word: '折',
      };
    }

    // 金额类型（默认：返赠、金额优惠、满减优惠等）
    default: {
      return {
        num: `${worth || ''}`,
        word: '元',
      };
    }
  }
};

export const getCouponLeft = <T extends Equity>(equity: T): CouponLeft => {
  switch (equity.virtualType) {
    case EquityType.COUPON: {
      const { couponType } = equity;

      if (
        [
          EquityCouponType.DISCOUNT,
          EquityCouponType.FREE,
          EquityCouponType.LADDER_DISCOUNT,
        ].includes(couponType)
      ) {
        return {
          num: `${equity.discount || 0}`,
          word: "折",
        };
      }

      return {
        num: `${equity.worth || 0}`,
        word: "元",
      };
    }
    case EquityType.RIDE_CARD: {
      return {
        num: `${equity.validDays}`,
        word: "天",
      };
    }
    case EquityType.SUPER_MEMBER_CARD: {
      return {
        num: "",
        word: "会员卡",
      };
    }
    case EquityType.EQUITY_CARD: {
      return {
        num: "",
        word: "权益卡",
      };
    }
    default:
      return {
        num: "",
        word: "",
      };
  }
};

/** 权益后端数据 => 前端展示数据 */
export const equityAdapter = <T extends Equity>(
  equity: T,
  options?: {
    descFormatter: Partial<
      Record<EquityDescType, (descValue: string, descEquity: T) => string>
    >;
  },
): T & {
  /** 权益描述列表 */
  descList: string[];
  /** 券左侧信息 */
  couponLeft: {
    /** 数字部分 */
    num: string;
    /** 文字部分 */
    word: string;
  };
  descMap: EquityDescMap;
} => {
  const {
    ruleType,
    validDays,
    endTimeDate,
    virtualType,
    maxDiscountAmount,
    couponThreshold,
    effectDays,
    commodityDesc = "",
  } = equity;
  const descList = [];
  const descMap: EquityDescMap = {};
  const { descFormatter = {} } = options || {};
  if (effectDays) {
    return {
      descList: commodityDesc.replace(/[，,。]+$/, "").split(/[,，。]/),
      couponLeft: {
        num: `${effectDays}`,
        word: "天",
      },
      descMap,
      ...equity,
      virtualNum: 1,
    };
  }
  if (virtualType === EquityType.COUPON) {
    const { couponType } = equity;

    switch (couponType) {
      case EquityCouponType.AMOUNT: {
        const desc = "使用金额无门槛";
        descList.push(desc);
        descMap.threshold = desc;
        break;
      }
      case EquityCouponType.DISCOUNT: {
        if (typeof maxDiscountAmount === "number" && maxDiscountAmount > 0) {
          const value = `${maxDiscountAmount}元`;
          const desc = typeof descFormatter.discountAmount === "function"
            ? descFormatter.discountAmount(value, equity)
            : `最高抵扣${value}`;
          descList.push(desc);
          descMap.discountAmount = desc;
        }
        break;
      }
      case EquityCouponType.FULL_AMOUNT: {
        if (typeof couponThreshold === "number" && couponThreshold > 0) {
          const value = `${couponThreshold}元`;
          const desc = typeof descFormatter.threshold === "function"
            ? descFormatter.threshold(value, equity)
            : `满${value}可用`;
          descList.push(desc);
          descMap.threshold = desc;
        }
        break;
      }
      default:
      // noop
    }
  }

  if (ruleType === 1 && typeof validDays === "number") {
    const value = `${validDays}天`;
    const desc = typeof descFormatter.validDays === "function"
      ? descFormatter.validDays(value, equity)
      : `${value}内有效`;
    descList.push(desc);
    descMap.validDays = desc;
  }

  if (ruleType === 0 && typeof endTimeDate === "number") {
    const value = timeFormat(endTimeDate);
    const desc = typeof descFormatter.validDate === "function"
      ? descFormatter.validDate(value, equity)
      : `有效期至${value}`;
    descList.push(desc);
    descMap.validDate = desc;
  }

  const couponLeft = getCouponLeft(equity);

  return {
    descList,
    couponLeft,
    descMap,
    ...equity,
  };
};

// 改造后的获取优惠券商城URL的函数
export const getCouponMallUrlNew = () => {
  if (process.env.NODE_ENV === "development") {
    return "http://172.19.160.143:8090/";
  }

  // 1. 获取当前页面URL的所有参数（包含空值）
  const currentParams = getUrlParams();
  // 2. 定义需要拼接的固定参数
  const fixedParams = {
    hiddenNavigationBar: '1',
    wloc: '1',
    pageTab: 'coupon',
  };
  // 3. 合并参数（固定参数覆盖同名的当前页面参数）
  const finalParams = { ...currentParams, ...fixedParams };

  // 4. 将参数对象转为URL参数字符串（不过滤任何空值）
  const paramStr = Object.entries(finalParams)
    .filter(([key]) => key) // 过滤空key
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`) // 直接编码，不过滤
    .join('&');

  const appEnv = getAppEnv();
  // 5. 拼接最终URL（有参数时才加?）
  const baseUrl = `https://m.hellobike.com/AppPlatformH5${
    appEnv !== "pro" ? `/${appEnv}` : ""
  }/latest/index.html#/welfare-center`;

  return paramStr ? `${baseUrl}?${paramStr}` : baseUrl;
};

/** 获取卡券商城页面url */
export const getCouponMallUrl = () => {
  if (process.env.NODE_ENV === "development") {
    return "http://172.19.160.143:8090/";
  }

  const appEnv = getAppEnv();
  return `https://m.hellobike.com/AppPlatformH5${
    appEnv !== "pro" ? `/${appEnv}` : ""
  }/latest/index.html`;
};

/** 获取卡券商城订单详情页url */
export const getCouponMallOrderDetailUrl = (orderId: string) => `${getCouponMallUrl()}#/order/${orderId}?${qs.stringify(appQueryConfig)}`;

/** 获取卡券商城订单列表url */
export const getCouponMallOrderListUrl = () => `${getCouponMallUrl()}#/order?${qs.stringify(appQueryConfig)}`;

export const equityGoUse = async (equity: OrderEquity) => {
  if (
    !(
      isHelloFamilyApp
      || equity.appOnly
      || (env as { isHarmony: boolean }).isHarmony
    )
  ) {
    goCouponPage();
    return;
  }

  try {
    uiMgr.showLoading();
    const { data } = await fetchCouponGouseConfig({
      batchCode: equity.virtualId,
    });
    const { targetUrl, toast } = data || {};

    if (isWeixin) {
      if (toast) {
        uiMgr.showToast(toast);
      } else {
        goCouponPage();
      }

      return;
    }

    if (targetUrl) {
      showThirdPage(targetUrl);
    } else {
      goCouponPage();
    }
  } catch (err) {
    uiMgr.showToast("当前无法跳转，请稍后重试");
  } finally {
    uiMgr.hideLoading();
  }
};

// 禁用IOS容器侧滑返回
export const disableInteractivePop = (disable: boolean) => {
  if (isEasyBikeApp && isIOS) {
    Native.callNative({
      classMap: "NavBar",
      method: "webViewInteractivePopDisabled",
      params: {
        disable,
      },
    });
    Native.callNative({
      classMap: "NavBar",
      method: "nativeInteractivePopDisabled",
      params: {
        disable,
      },
    });
  }
};
